import React, { useCallback, useEffect, useState } from 'react';

import { Button, Table } from '@shared/components/bootstrap';
import { FEATURE_FLAG } from '@shared/config/feature_flag';

import {
  Dialog as ConfirmationDialog,
  Response as ConfirmationResponse,
  useConfirmation,
} from '@admin/components/confirmation';
import { Dialog as ErrorDialog } from '@admin/components/errors/dialog';
import { Panel } from '@admin/components/helpers/panel';
import { Spinner } from '@admin/components/spinner';

import {
  createTicketGroup,
  destroyTicketGroup,
  extractErrorMessage,
  fetchCallGroups,
  fetchEditableTicketGroups,
  fetchUsers,
  updateTicketGroup,
} from './api';
import { SearchInput } from './search_input';
import {
  TicketGroupFormState,
  TicketManagementCallGroup,
  TicketManagementTicketGroup,
  TicketManagementUser,
} from './types';

const emptyForm = (): TicketGroupFormState => ({
  name: '',
  internal_ticket_assignable: false,
  customer_ticket_assignable: false,
  call_group_id: null,
  users: [],
});

const MembersList: React.FC<{
  users: TicketManagementUser[];
  editable: boolean;
  onUnassign?(user: TicketManagementUser): void;
}> = ({ users, editable, onUnassign }) => {
  if (!users.length) {
    return null;
  }

  return (
    <table className="table table-condensed table-nonfluid mar-no">
      <tbody>
        {users.map((user) => (
          <tr key={user.id}>
            <td className="col-zero col-nowrap text-left">
              {user.active ? <span>{user.name}</span> : <del>{user.name}</del>}
            </td>
            {editable && onUnassign && (
              <td className="col-zero col-nowrap text-right">
                <button type="button" className="btn btn-default" onClick={() => onUnassign(user)}>
                  -
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const TicketGroupsPanel: React.FC = () => {
  const { confirm, confirmation } = useConfirmation();
  const [groups, setGroups] = useState<TicketManagementTicketGroup[]>();
  const [users, setUsers] = useState<TicketManagementUser[]>([]);
  const [callGroups, setCallGroups] = useState<TicketManagementCallGroup[]>([]);
  const [selection, setSelection] = useState<TicketManagementTicketGroup | null>(null);
  const [form, setForm] = useState<TicketGroupFormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  const loadGroups = useCallback(async () => {
    const data = await fetchEditableTicketGroups();
    setGroups(data);
  }, []);

  useEffect(() => {
    void loadGroups();
    void fetchUsers().then(setUsers);
    if (FEATURE_FLAG.new_ivr_flow_2022_06) {
      void fetchCallGroups().then(setCallGroups);
    }
  }, [loadGroups]);

  const cancel = () => {
    setSelection(null);
    setForm(null);
  };

  const edit = (group: TicketManagementTicketGroup) => {
    setSelection(group);
    setForm({
      id: group.id,
      name: group.name,
      internal_ticket_assignable: group.internal_ticket_assignable,
      customer_ticket_assignable: group.customer_ticket_assignable,
      call_group_id: group.call_group_id,
      users: [...group.users],
    });
  };

  const add = () => {
    setSelection(null);
    setForm(emptyForm());
  };

  const assignUser = (option: { id: number; name: string }) => {
    const user = users.find((existing) => existing.id === option.id);
    if (!form || !user || form.users.some((existing) => existing.id === user.id)) {
      return;
    }

    setForm({ ...form, users: [...form.users, user] });
  };

  const unassignUser = (user: TicketManagementUser) => {
    if (!form) return;
    setForm({ ...form, users: form.users.filter((existing) => existing.id !== user.id) });
  };

  const save = async () => {
    if (!form) return;

    setSaving(true);
    try {
      if (selection) {
        const updated = await updateTicketGroup(form);
        setGroups((current) => current?.map((group) => (group.id === selection.id ? updated : group)));
      } else {
        const created = await createTicketGroup(form);
        setGroups((current) => [...(current ?? []), created]);
      }
      cancel();
    } catch (requestError) {
      setError(extractErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (group: TicketManagementTicketGroup) => {
    if (group.default) return;

    const confirmed = await confirm({
      title: 'Are You Sure?',
      description:
        'Removing this group will remove it from any rules it is associated with. ' +
        `Are you sure you want to delete "${group.name}"?`,
      confirm: 'Continue',
      cancel: 'Cancel',
    });

    if (confirmed !== ConfirmationResponse.Confirm) return;

    try {
      await destroyTicketGroup(group.id);
      setGroups((current) => current?.filter((existing) => existing.id !== group.id));
    } catch (requestError) {
      setError(extractErrorMessage(requestError));
    }
  };

  const showCallRouting = FEATURE_FLAG.new_ivr_flow_2022_06;

  return (
    <>
      {confirmation && <ConfirmationDialog confirmation={confirmation} />}
      {error && <ErrorDialog title="Whoops" description={error} onClose={() => setError(undefined)} />}

      <Panel id="ticketgroups">
        <Panel.Header>
          <Panel.Title>Ticket Groups</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          {groups ? (
            <Table>
              <thead>
                <tr>
                  <th className="col-sm-4 text-left">Name</th>
                  <th className="col-sm-1 text-center">Internal Task Tickets</th>
                  <th className="col-sm-1 text-center">Customer Tickets</th>
                  <th className="col-sm-2 text-left">Members</th>
                  {showCallRouting && <th className="col-sm-2 text-left">Call Routing</th>}
                  <th className="col-sm-2 text-right col-nowrap" />
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => {
                  const editing = selection?.id === group.id && !!form;

                  return (
                    <tr key={group.id} id={`ticket-group-${group.id}`}>
                      <td className="col-sm-4 text-left">
                        {editing ? (
                          <input
                            name="group[name]"
                            className="form-control"
                            type="text"
                            value={form.name}
                            disabled={group.default}
                            onChange={(event) => setForm({ ...form, name: event.target.value })}
                          />
                        ) : (
                          <span>{group.name}</span>
                        )}
                      </td>
                      <td className="col-sm-1 text-center">
                        {editing ? (
                          <input
                            type="checkbox"
                            checked={form.internal_ticket_assignable}
                            onChange={(event) => setForm({ ...form, internal_ticket_assignable: event.target.checked })}
                          />
                        ) : (
                          <input type="checkbox" checked={group.internal_ticket_assignable} disabled />
                        )}
                      </td>
                      <td className="col-sm-1 text-center">
                        {editing ? (
                          <input
                            type="checkbox"
                            checked={form.customer_ticket_assignable}
                            onChange={(event) => setForm({ ...form, customer_ticket_assignable: event.target.checked })}
                          />
                        ) : (
                          <input type="checkbox" checked={group.customer_ticket_assignable} disabled />
                        )}
                      </td>
                      <td className="col-sm-2 text-left">
                        {editing ? (
                          <>
                            <MembersList users={form.users} editable onUnassign={unassignUser} />
                            <SearchInput options={users} placeholder="Search" onSelect={assignUser} />
                          </>
                        ) : (
                          <MembersList users={group.users} editable={false} />
                        )}
                      </td>
                      {showCallRouting && (
                        <td className="col-sm-2 text-left">
                          {editing ? (
                            <select
                              className="form-control"
                              value={form.call_group_id ?? ''}
                              onChange={(event) =>
                                setForm({
                                  ...form,
                                  call_group_id: event.target.value ? Number(event.target.value) : null,
                                })
                              }
                            >
                              <option value="">-- Call Group --</option>
                              {callGroups.map((callGroup) => (
                                <option key={callGroup.id} value={callGroup.id}>
                                  {callGroup.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <select className="form-control" value={group.call_group_id ?? ''} disabled>
                              <option value="">-- Call Group --</option>
                              {callGroups.map((callGroup) => (
                                <option key={callGroup.id} value={callGroup.id}>
                                  {callGroup.name}
                                </option>
                              ))}
                            </select>
                          )}
                        </td>
                      )}
                      <td className="col-sm-2 text-right col-nowrap">
                        {!editing && (
                          <Button kind="default" onClick={() => edit(group)}>
                            Edit
                          </Button>
                        )}
                        {editing && (
                          <>
                            <Button kind="default" onClick={cancel} disabled={saving}>
                              Cancel
                            </Button>{' '}
                            <Button kind="primary" onClick={() => void save()} loading={saving}>
                              Save
                            </Button>
                          </>
                        )}
                        {!editing && (
                          <>
                            {' '}
                            <Button kind="danger" disabled={group.default} onClick={() => void remove(group)}>
                              Remove
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {form && !selection && (
                  <tr>
                    <td className="col-sm-4 text-left">
                      <input
                        name="group[name]"
                        className="form-control"
                        type="text"
                        value={form.name}
                        onChange={(event) => setForm({ ...form, name: event.target.value })}
                      />
                    </td>
                    <td className="col-sm-6 text-left" colSpan={showCallRouting ? 4 : 3}>
                      <MembersList users={form.users} editable onUnassign={unassignUser} />
                      <SearchInput options={users} placeholder="Search" onSelect={assignUser} />
                    </td>
                    <td className="col-sm-2 text-right col-nowrap">
                      <Button kind="default" onClick={cancel} disabled={saving}>
                        Cancel
                      </Button>{' '}
                      <Button kind="primary" onClick={() => void save()} loading={saving}>
                        Save
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          ) : (
            <Spinner />
          )}
        </Panel.Body>
        <Panel.Footer align="right">
          <Button kind="default" onClick={add} disabled={!!form}>
            Add
          </Button>
        </Panel.Footer>
      </Panel>
    </>
  );
};
